import React from 'react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';

interface GradientLineChartProps {
  trendValues: any;
  keyName: string;
  screenWidth: any;
  isLandscape: boolean;
  insightType: string;
  visibleInsights: string[];
  postionLeft?: number;
  topOffset?: string;
}

const GradientLineChart: React.FC<GradientLineChartProps> = ({
  trendValues,
  keyName,
  screenWidth,
  isLandscape,
  insightType,
  visibleInsights,
  postionLeft = 100,
  topOffset,
}) => {
  if (!visibleInsights.includes(insightType)) {
    return null;
  }
  const {
    monthTrendValue,
    weekTrendValue,
    dayTrendValue,
    eightHoursTrendValue,
    fourHoursTrendValue,
    oneHourTrendValue,
  } = trendValues;

  let dataArrTrends = [
    monthTrendValue,
    weekTrendValue,
    dayTrendValue,
    eightHoursTrendValue,
    fourHoursTrendValue,
    oneHourTrendValue,
  ];

  // Require a real numeric value — stubs like `{ value: undefined, ntile: undefined }`
  // still have `'ntile' in item` and used to leak into the plot pipeline.
  dataArrTrends = dataArrTrends.filter(
    (item) =>
      item &&
      typeof item === 'object' &&
      typeof item.value === 'number' &&
      Number.isFinite(item.value)
  );

  const NTILE_COLOR_MAP: Record<number, string> = {
    1: '#2a74a2',
    2: '#acd6f0',
    3: '#daeffc',
    4: '#fbdded',
    5: '#ed6db2',
    6: '#ff229c',
  };

  const getTrendColorFromNtile = (ntile?: number) =>
    ntile ? NTILE_COLOR_MAP[ntile] || '#ccc' : '#ccc';

  const lineDataArray = dataArrTrends
    .map((element: any, index) => ({
      value: element.value,
      name: `Page ${index}`,
      showarrow: index === dataArrTrends.length - 1,
      color: getTrendColorFromNtile(element.ntile),
      ntile: element.ntile,
    }))
    .filter((item) => item.value > 0);

  const getGradientStops = (data: typeof lineDataArray) => {
    const stops: { offset: string; color: string; ntile: number }[] = [];
    const total = data.length - 1;

    stops.push({ offset: '0%', color: data[0].color, ntile: data[0].ntile });

    for (let i = 1; i <= total; i++) {
      const offsetPercent = (i / total) * 100;
      stops.push({
        offset: `${offsetPercent.toFixed(2)}%`,
        color: data[i].color,
        ntile: data[i].ntile,
      });
    }

    return stops;
  };

  if (lineDataArray.length <= 1) {
    return null;
  }

  const stops = getGradientStops(lineDataArray);
  return (
    <div
      style={{
        position: 'absolute',
        left: postionLeft,
        top: topOffset ?? undefined,
        transform: topOffset ? 'translateY(-50%)' : undefined,
        height: '30px',
        zIndex: 2,
        width: `${screenWidth * (lineDataArray.length - 1) + 40}px`,
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={lineDataArray} stackOffset="sign">
          <defs>
            <linearGradient
              id={`lineGradient-${keyName}-${insightType}`}
              x1="0"
              y1="0"
              x2="1"
              y2="0"
            >
              {stops.map((stop, idx) => (
                <stop key={idx} offset={stop.offset} stopColor={stop.color} stopOpacity={0.7} />
              ))}
            </linearGradient>
          </defs>
          <Line
            type="monotone"
            dataKey="value"
            stroke={`url(#lineGradient-${keyName}-${insightType})`}
            strokeWidth={3}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default GradientLineChart;
